import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ApplicationForm({ 
  applicationType, 
  title, 
  description,
  additionalFields = [],
  showPortfolio = true,
  showExperience = true,
  showRequirements = false,
  showInsurance = false,
  showCertificates = false,
}) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postcode: '',
    experience: '',
    portfolio_links: '',
    social_media: '',
    description: '',
    requirements: '',
    availability: '',
    has_insurance: false,
    has_certificates: false,
    certificate_types: '',
    additional_info: '',
    agrees_to_terms: false,
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles([...files, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agrees_to_terms) {
      toast.error('You must agree to the terms and conditions');
      return;
    }
    
    setLoading(true);
    
    try {
      const submitData = new FormData();
      submitData.append('application_type', applicationType);
      
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      
      files.forEach(file => {
        submitData.append('files', file);
      });
      
      await axios.post(`${API}/applications`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Application submitted successfully! We\'ll be in touch.');
      
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        postcode: '',
        experience: '',
        portfolio_links: '',
        social_media: '',
        description: '',
        requirements: '',
        availability: '',
        has_insurance: false,
        has_certificates: false,
        certificate_types: '',
        additional_info: '',
        agrees_to_terms: false,
      });
      setFiles([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ff-blue pt-20" data-testid={`application-${applicationType}-page`}>
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <span className="badge mb-4">Applications</span>
            <h1 className="font-display text-4xl sm:text-5xl text-ff-brown mb-4">
              {title}
            </h1>
            <p className="text-ff-brown/70 max-w-2xl mx-auto">
              {description}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/40 backdrop-blur-sm rounded-3xl p-8"
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="application-form">
              {/* Personal Information */}
              <div>
                <h3 className="font-display text-xl text-ff-brown mb-4">Contact Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className="form-input"
                      required
                      data-testid="app-first-name"
                    />
                  </div>
                  <div>
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className="form-input"
                      required
                      data-testid="app-last-name"
                    />
                  </div>
                  <div>
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      required
                      data-testid="app-email"
                    />
                  </div>
                  <div>
                    <label className="form-label">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      required
                      data-testid="app-phone"
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="form-input"
                    data-testid="app-address"
                  />
                </div>
                
                <div className="grid md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">State</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="NSW"
                    />
                  </div>
                  <div>
                    <label className="form-label">Postcode</label>
                    <input
                      type="text"
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div>
                <label className="form-label">Social Media Links</label>
                <input
                  type="text"
                  name="social_media"
                  value={formData.social_media}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Instagram, Facebook, etc."
                  data-testid="app-social-media"
                />
              </div>

              {/* Experience */}
              {showExperience && (
                <div>
                  <h3 className="font-display text-xl text-ff-brown mb-4">Experience</h3>
                  <label className="form-label">Previous Experience *</label>
                  <textarea
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className="form-input min-h-[120px] resize-none"
                    placeholder="Tell us about your relevant experience, previous festivals/events..."
                    required
                    data-testid="app-experience"
                  />
                </div>
              )}

              {/* Portfolio */}
              {showPortfolio && (
                <div>
                  <label className="form-label">Portfolio/Work Links</label>
                  <textarea
                    name="portfolio_links"
                    value={formData.portfolio_links}
                    onChange={handleChange}
                    className="form-input min-h-[80px] resize-none"
                    placeholder="SoundCloud, YouTube, Website, etc. (one per line)"
                    data-testid="app-portfolio"
                  />
                </div>
              )}

              {/* Description */}
              <div>
                <label className="form-label">About Your Work/Proposal *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="form-input min-h-[150px] resize-none"
                  placeholder="Describe what you'd like to bring to Frequency Forest..."
                  required
                  data-testid="app-description"
                />
              </div>

              {/* Requirements */}
              {showRequirements && (
                <div>
                  <label className="form-label">Technical Requirements</label>
                  <textarea
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleChange}
                    className="form-input min-h-[100px] resize-none"
                    placeholder="Power, space, equipment needs..."
                    data-testid="app-requirements"
                  />
                </div>
              )}

              {/* Availability */}
              <div>
                <label className="form-label">Availability</label>
                <textarea
                  name="availability"
                  value={formData.availability}
                  onChange={handleChange}
                  className="form-input min-h-[80px] resize-none"
                  placeholder="When are you available during the festival? Any restrictions?"
                  data-testid="app-availability"
                />
              </div>

              {/* Insurance */}
              {showInsurance && (
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="has_insurance"
                    checked={formData.has_insurance}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, has_insurance: checked })
                    }
                  />
                  <label htmlFor="has_insurance" className="text-ff-brown/70 text-sm">
                    I have public liability insurance
                  </label>
                </div>
              )}

              {/* Certificates */}
              {showCertificates && (
                <div>
                  <div className="flex items-center space-x-3 mb-4">
                    <Checkbox
                      id="has_certificates"
                      checked={formData.has_certificates}
                      onCheckedChange={(checked) => 
                        setFormData({ ...formData, has_certificates: checked })
                      }
                    />
                    <label htmlFor="has_certificates" className="text-ff-brown/70 text-sm">
                      I have relevant certificates (RSA, First Aid, White Card, etc.)
                    </label>
                  </div>
                  {formData.has_certificates && (
                    <div>
                      <label className="form-label">List your certificates</label>
                      <input
                        type="text"
                        name="certificate_types"
                        value={formData.certificate_types}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="RSA, First Aid Level 2, etc."
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Additional Info */}
              <div>
                <label className="form-label">Additional Information</label>
                <textarea
                  name="additional_info"
                  value={formData.additional_info}
                  onChange={handleChange}
                  className="form-input min-h-[80px] resize-none"
                  placeholder="Anything else you'd like us to know?"
                  data-testid="app-additional-info"
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="form-label">Upload Files (ID, Portfolio, Certificates)</label>
                <div className="border-2 border-dashed border-ff-outline/30 rounded-xl p-6 text-center hover:border-ff-yellow transition-colors bg-white/30">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.mp3,.wav"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-ff-brown/40 mx-auto mb-2" />
                    <p className="text-ff-brown/70 text-sm">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-ff-brown/40 text-xs mt-1">
                      PDF, Images, Documents, Audio (Max 10MB each)
                    </p>
                  </label>
                </div>
                
                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-white/50 p-3 rounded-lg">
                        <span className="text-ff-brown/70 text-sm truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-600 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Terms Agreement */}
              <div className="border-t border-ff-outline/20 pt-6">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="agrees_to_terms"
                    checked={formData.agrees_to_terms}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, agrees_to_terms: checked })
                    }
                    required
                  />
                  <label htmlFor="agrees_to_terms" className="text-ff-brown/70 text-sm">
                    I agree to the Frequency Forest terms and conditions, and understand that 
                    submitting this application does not guarantee acceptance. I confirm that 
                    all information provided is accurate and I consent to being contacted 
                    regarding my application. *
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="app-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
                Submit Application
              </button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-8 text-center"
          >
            <p className="text-ff-brown/50 text-sm">
              Questions? Reach out via our{' '}
              <a href="/faq-contact" className="text-ff-brown hover:underline">
                contact page
              </a>
              {' '}or find us on social media.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
